import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Paper, Portal, Typography } from "@mui/material";

/**
 * Tiptap 본문 @ 멘션 자동완성 — Portal Popper 기반 컴포넌트.
 *
 * <h3>왜 컴포넌트화했는가</h3>
 * 이전엔 {@link RichTextEditor} 내부에 300줄짜리 인라인 로직(상태 6개, useEffect 3개, Portal+Paper 렌더)
 * 으로 매몰되어 있었다. 채널에만 쓰이고 메일/결재 본문은 멘션 자체가 없어 통합 부수효과(멘션 알람) 가
 * 동작하지 않는 격차가 생겼다. 본 컴포넌트는 RichTextEditor 의 멘션 동작을 한 곳으로 추출해
 * 채널·메일·향후 다른 본문에서 동일 UX 로 사용하도록 한다.
 *
 * <h3>props</h3>
 * <ul>
 *   <li>{@code editor} — Tiptap editor 인스턴스. 없으면 noop.</li>
 *   <li>{@code members} — 정적 채널 멤버 배열 (이전과 동일 형태: {id, name, deptName, positionName} or {employee: {...}}).
 *       <b>OR</b> {@code fetcher(query) => Promise&lt;array&gt;} — 비동기 모드 (메일 본문에서 사용).</li>
 *   <li>{@code maxItems=8} — 표시 후보 최대 개수</li>
 *   <li>{@code onPicked(emp, name)} — 선택 시 콜백. 외부에서 멘션 발송 등 부수효과 트리거용.</li>
 *   <li>{@code debounceMs=300} — fetcher 모드 디바운스</li>
 * </ul>
 *
 * <h3>활성/비활성</h3>
 * members 도 fetcher 도 없으면 마운트 자체에서 아무 동작 안 함 — 기존 RichTextEditor 의
 * "mentionMembers 비어있으면 비활성" 정책 유지.
 */
export default function MentionAutocomplete({
    editor,
    members,
    fetcher,
    maxItems = 8,
    onPicked,
    debounceMs = 300,
}) {
    const enabled = (Array.isArray(members) && members.length > 0) || typeof fetcher === "function";

    // popper 좌표 + 현재 쿼리. cursorTop/Bottom 은 viewport 좌표로 보관 (flip 판단용)
    const [mentionState, setMentionState] = useState({
        open: false, query: "", cursorTop: 0, cursorBottom: 0, left: 0,
    });
    const [activeIdx, setActiveIdx] = useState(0);

    // 후보 — 정적 모드는 즉시 필터, 비동기 모드는 debounce 후 fetcher 호출.
    const [asyncOpts, setAsyncOpts] = useState([]);
    const debounceRef = useRef(null);

    const filtered = useMemo(() => {
        if (!enabled) return [];
        if (typeof fetcher === "function") return asyncOpts.slice(0, maxItems);
        // 대소문자 무시 — 양쪽 모두 소문자화(영문 이름 '@John' 이 'john' 입력에도 매칭)
        const q = mentionState.query.toLowerCase();
        return (members || []).filter(m => {
            const name = (m.employee?.name || m.name || "").toString().toLowerCase();
            return name.includes(q);
        }).slice(0, maxItems);
    }, [enabled, members, fetcher, asyncOpts, mentionState.query, maxItems]);

    // 비동기 fetcher — query 변경 시 debounce 후 호출
    useEffect(() => {
        if (!enabled || typeof fetcher !== "function" || !mentionState.open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetcher(mentionState.query);
                setAsyncOpts(Array.isArray(res) ? res : []);
            } catch {
                setAsyncOpts([]);
            }
        }, debounceMs);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [enabled, fetcher, mentionState.open, mentionState.query, debounceMs]);

    // 커서 직전 텍스트가 @쿼리$ 패턴이면 popper open
    useEffect(() => {
        if (!editor || !enabled) return;
        const update = () => {
            const { state, view } = editor;
            const { from, empty } = state.selection;
            if (!empty) { setMentionState(s => s.open ? { ...s, open: false } : s); return; }
            const $pos = state.doc.resolve(from);
            if (!$pos.parent?.isTextblock) { setMentionState(s => s.open ? { ...s, open: false } : s); return; }
            const textBefore = $pos.parent.textBetween(0, $pos.parentOffset, undefined, "​");
            const m = textBefore.match(/@([^\s@]*)$/);
            if (!m) { setMentionState(s => s.open ? { ...s, open: false } : s); return; }
            const coords = view.coordsAtPos(from);
            setMentionState({
                open: true,
                query: m[1],
                cursorTop: coords.top,
                cursorBottom: coords.bottom,
                left: coords.left,
            });
            setActiveIdx(0);
        };
        editor.on("selectionUpdate", update);
        editor.on("update", update);
        return () => {
            editor.off("selectionUpdate", update);
            editor.off("update", update);
        };
    }, [editor, enabled]);

    const insertMention = useCallback((emp, name) => {
        if (!editor || !name) return;
        const { state } = editor;
        const { from } = state.selection;
        const $pos = state.doc.resolve(from);
        const textBefore = $pos.parent.textBetween(0, $pos.parentOffset, undefined, "​");
        const m = textBefore.match(/@([^\s@]*)$/);
        if (!m) return;
        const start = from - m[0].length;
        editor.chain().focus()
            .deleteRange({ from: start, to: from })
            .insertContent(`@${name} `)
            .run();
        setMentionState(s => ({ ...s, open: false }));
        onPicked?.(emp, name);
    }, [editor, onPicked]);

    // 키보드 네비 — popper 열린 동안에만 capture
    useEffect(() => {
        if (!mentionState.open || filtered.length === 0) return;
        const onKey = (e) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIdx(i => (i + 1) % filtered.length);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIdx(i => (i - 1 + filtered.length) % filtered.length);
            } else if (e.key === "Enter" || e.key === "Tab") {
                const picked = filtered[activeIdx];
                const emp = picked?.employee || picked;
                const name = emp?.name;
                if (name) { e.preventDefault(); insertMention(emp, name); }
            } else if (e.key === "Escape") {
                e.preventDefault();
                setMentionState(s => ({ ...s, open: false }));
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [mentionState.open, filtered, activeIdx, insertMention]);

    if (!enabled || !mentionState.open || filtered.length === 0) return null;

    const MAX_H = 280;
    const spaceBelow = window.innerHeight - mentionState.cursorBottom;
    const spaceAbove = mentionState.cursorTop;
    const openAbove = spaceBelow < MAX_H && spaceAbove > spaceBelow;

    return (
        <Portal>
            <Paper elevation={4} sx={{
                position: "fixed",
                left: mentionState.left,
                ...(openAbove
                    ? { bottom: window.innerHeight - mentionState.cursorTop + 4 }
                    : { top: mentionState.cursorBottom + 4 }),
                zIndex: (theme) => theme.zIndex.modal + 100,
                minWidth: 220, maxWidth: 320,
                maxHeight: MAX_H,
                overflowY: "auto",
                borderRadius: 1.5,
                border: "1px solid #e2e8f0",
                boxShadow: "0 6px 24px rgba(0,0,0,0.14)",
            }}>
                {filtered.map((m, i) => {
                    const emp = m.employee || m;
                    const active = i === activeIdx;
                    // 부서·직책 키 호환 (mentionMembers 형식 vs /api/employees/suggest 형식)
                    const dept = emp.deptName || emp.department || "";
                    const pos  = emp.positionName || emp.position || "";
                    return (
                        <Box
                            key={emp.id ?? emp.employeeCode ?? `${emp.name}-${i}`}
                            onMouseDown={(e) => { e.preventDefault(); insertMention(emp, emp.name); }}
                            onMouseEnter={() => setActiveIdx(i)}
                            sx={{
                                px: 1.5, py: 0.9, cursor: "pointer",
                                display: "flex", alignItems: "center", gap: 1.2,
                                bgcolor: active ? "#eff6ff" : "transparent",
                                "&:hover": { bgcolor: "#eff6ff" },
                            }}
                        >
                            <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: "#dbeafe", color: "#1d4ed8" }}>
                                {emp.name?.[0]}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, color: "#0f172a" }}>
                                    {emp.name}
                                </Typography>
                                {(dept || pos) && (
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                        {[dept, pos].filter(Boolean).join(" · ")}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Paper>
        </Portal>
    );
}
